import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DownArrowIcon } from './CXForm/assets/images';
export type Option = { text: string; value: string };

interface ISingleSelectPlainProps {
    options: string[];
    value?: string;
    disabled?: boolean;
    loading?: boolean;
    loadingText?: string;
    placeholder?: string;
    searchPlaceholder?: string;

    maxOptions?: number; // default 25 (not searching)
    maxResults?: number; // default 200 (searching)
    debounceMs?: number; // default 120
    resetKey?: number | string;
    serverSideSearch?: boolean;
    onSearchKeywordChange?: (keyword: string) => void;
    onDropdownVisibleChange?: (visible: boolean) => void;
    infiniteScroll?: boolean;
    hasMore?: boolean;
    onReachListEnd?: () => void;
    noResultsText?: string;

    onSelectOption?: (option: string) => void;

    // optional compatibility with old contract
    onChangeField?: (
        value: Option[],
        field: string,
        state: any,
        setState: (next: any) => void,
    ) => void;
    field?: string;
    state?: any;
    onChange?: (next: any) => void;

    wrapperClassName?: string;
    zIndex?: number;
}

const SingleSelectPlain: React.FC<ISingleSelectPlainProps> = ({
    options: options,
    value,
    disabled = false,
    loading = false,
    loadingText = 'Loading...',
    placeholder = 'Select template',
    searchPlaceholder = '',
    maxOptions = 25,
    maxResults = 200,
    debounceMs = 120,
    resetKey,
    serverSideSearch = false,
    onSearchKeywordChange,
    onDropdownVisibleChange,
    infiniteScroll = false,
    hasMore = false,
    onReachListEnd,
    noResultsText = 'No results found',
    onSelectOption: onSelectOption,
    onChangeField,
    field = '',
    state,
    onChange,
    wrapperClassName,
    zIndex = 1039,
}) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);
    const suppressSearchEmitRef = useRef(false);
    const didNotifyOpenRef = useRef(false);
    const loadMoreLockRef = useRef(false);

    const [open, setOpen] = useState(false);
    const [rawQuery, setRawQuery] = useState('');
    const [query, setQuery] = useState('');
    const isHardDisabled = disabled;
    const isBusy = loading;
    const isInputLocked = isHardDisabled;

    // debounce search
    useEffect(() => {
        const t = setTimeout(() => setQuery(rawQuery), debounceMs);
        return () => clearTimeout(t);
    }, [rawQuery, debounceMs]);

    useEffect(() => {
        if (!serverSideSearch) return;
        if (suppressSearchEmitRef.current) {
            if (query.trim().length === 0) {
                suppressSearchEmitRef.current = false;
                return;
            }
            suppressSearchEmitRef.current = false;
        }
        onSearchKeywordChange?.(query.trim());
    }, [query, serverSideSearch, onSearchKeywordChange]);

    useEffect(() => {
        if (!didNotifyOpenRef.current) {
            didNotifyOpenRef.current = true;
            return;
        }
        onDropdownVisibleChange?.(open);
    }, [open, onDropdownVisibleChange]);

    useEffect(() => {
        if (!isBusy) loadMoreLockRef.current = false;
    }, [isBusy]);

    useEffect(() => {
        if (!open) loadMoreLockRef.current = false;
    }, [open, query, hasMore]);

    useEffect(() => {
        if (!serverSideSearch || !open) return;
        if (!listRef.current) return;
        listRef.current.scrollTop = 0;
        loadMoreLockRef.current = false;
    }, [serverSideSearch, open, rawQuery]);

    const clearSearchInput = (suppressSearchEmit = false) => {
        if (suppressSearchEmit) suppressSearchEmitRef.current = true;
        setRawQuery('');
        setQuery('');
    };

    // external reset
    useEffect(() => {
        setOpen(false);
        clearSearchInput(true);
    }, [resetKey]);

    // close on outside click
    useEffect(() => {
        const onDocMouseDown = (e: MouseEvent) => {
            if (!rootRef.current) return;
            if (rootRef.current.contains(e.target as Node)) return;
            setOpen(false);
            // Outside close should restore default list for server-side search.
            clearSearchInput(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, []);

    const normalizedOptions = useMemo(
        () => (Array.isArray(options) ? options.filter(Boolean) : []),
        [options],
    );

    const isSearching = query.trim().length > 0;
    const list = useMemo(() => {
        if (serverSideSearch) return normalizedOptions;

        const kw = query.trim().toUpperCase();
        if (!kw) return normalizedOptions.slice(0, maxOptions);

        const out: string[] = [];
        for (let i = 0; i < normalizedOptions.length; i++) {
            const v = normalizedOptions[i];
            if ((v || '').toUpperCase().includes(kw)) {
                out.push(v);
                if (out.length >= maxResults) break;
            }
        }
        return out;
    }, [serverSideSearch, normalizedOptions, query, maxOptions, maxResults]);

    const pick = (option: string) => {
        if (isInputLocked) return;

        // single select -> replace
        onSelectOption?.(option);
        onChangeField?.(option ? [{ text: option, value: option }] : [], field, state, onChange || (() => { }));

        // after pick: close and clear search so next open shows default 25
        setOpen(false);
        clearSearchInput(true);

        // keep input displaying selected value
        if (inputRef.current) inputRef.current.blur();
    };

    const onBlur = () => {
        if (open) {
            setTimeout(() => {
                setOpen(false);
                clearSearchInput(true);
            }, 200);
        }
    };

    const displayValue = useMemo(() => {
        // when dropdown open, show user's search keyword; when closed, show selected value
        if (open) return rawQuery;
        return value || '';
    }, [open, rawQuery, value]);
    const selectedValue = String(value ?? '').trim();
    const showInitialLoading = isBusy && list.length === 0;
    const showLoadMoreLoading = isBusy && list.length > 0;

    const onListScroll = (e: React.UIEvent<HTMLUListElement>) => {
        if (!infiniteScroll || !hasMore || isBusy) return;
        if (loadMoreLockRef.current) return;

        const target = e.currentTarget;
        const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (distanceToBottom > 24) return;

        loadMoreLockRef.current = true;
        onReachListEnd?.();
    };

    const rootClassName = [
        'single-select',
        isHardDisabled ? 'single-select--disabled' : '',
        showInitialLoading ? 'single-select--loading' : '',
        wrapperClassName || '',
        value ? 'has-value' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            ref={rootRef}
            className={rootClassName}
            style={{
                position: 'relative',
                zIndex,
                ...(isHardDisabled ? { pointerEvents: 'none' as const, opacity: 0.6 } : {}),
            }}
        >
            <div className={`cx-dropdown ${wrapperClassName ? wrapperClassName : ''}`} style={{ zIndex: zIndex ?? 1039 }}>
                <div className="cx-form-control">
                    <input
                        ref={inputRef}
                        className={`cx-form-input ${open || !!value ? 'focused' : ''} ${isHardDisabled ? 'cx-disabled' : ''}`}
                        value={displayValue}
                        placeholder={open ? searchPlaceholder : ''}
                        onClick={(e) => {
                            e.preventDefault();
                            if (isHardDisabled) return;

                            setOpen((prev) => !prev);
                            setTimeout(() => {
                                inputRef.current?.focus();
                                inputRef.current?.select();
                            }, 0);
                        }}
                        onBlur={onBlur}
                        onChange={(e) => {
                            if (isInputLocked) return;
                            setOpen(true);
                            setRawQuery(e.target.value || '');
                        }}
                    />

                    {(open || !!value) ? (
                        <div>
                            <div className="focused-label" onMouseDown={(e) => {
                                e.preventDefault();
                                if (isHardDisabled) return;
                                setOpen(true);
                                setTimeout(() => inputRef.current?.focus(), 0);
                            }}>
                                {placeholder}
                            </div>
                        </div>
                    ) : (
                        <div
                            className={`not-focused-label ${isHardDisabled ? 'cx-disabled' : ''}`}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                if (isHardDisabled) return;
                                setOpen(true);
                                setTimeout(() => inputRef.current?.focus(), 0);
                            }}
                        >
                            {placeholder}
                        </div>
                    )}

                    <DownArrowIcon
                        className={`cx-down-arrow-icon ${isInputLocked ? 'cx-dropdown-item-disabled' : ''}`}
                        height={24}
                        width={24}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            if (isHardDisabled) return;

                            setOpen((prev) => !prev);
                            setTimeout(() => inputRef.current?.focus(), 0);
                        }}
                    />
                </div>

                {open && (
                    <div className={`cx-dropdown-body single-select__dropdown-body open`} style={{ width: '100%' }}>
                        <ul
                            ref={listRef}
                            className="cx-dropdown-list single-select__dropdown-list"
                            onScroll={onListScroll}
                        >
                            {showInitialLoading ? (
                                <li>
                                    <button className="cx-dropdown-item cx-dropdown-item-disabled single-select__loading-item" onMouseDown={(e) => e.preventDefault()}>
                                        <span className="single-select__loading-spinner" />
                                        <span>{loadingText}</span>
                                    </button>
                                </li>
                            ) : list.length !== 0 ? (
                                list.map((option, index) => (
                                    <li key={`${option}-${index}`}>
                                        <button
                                            className={`cx-dropdown-item ${String(option ?? '').trim() === selectedValue ? 'selected' : ''}`}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                pick(option);
                                            }}
                                        >
                                            {option}
                                        </button>
                                    </li>
                                ))
                            ) : (
                                <li>
                                    <button className="cx-dropdown-item single-select__empty-state" onMouseDown={(e) => e.preventDefault()}>
                                        {noResultsText}
                                    </button>
                                </li>
                            )}

                            {!serverSideSearch && !isSearching && normalizedOptions.length > maxOptions && (
                                <li>
                                    <button className="single-select__hint" onMouseDown={(e) => e.preventDefault()}>
                                        Only showing first {maxOptions} records. Type to search more.
                                    </button>
                                </li>
                            )}

                            {showLoadMoreLoading && (
                                <li>
                                    <button className="cx-dropdown-item cx-dropdown-item-disabled single-select__loading-item" onMouseDown={(e) => e.preventDefault()}>
                                        <span className="single-select__loading-spinner" />
                                        <span>{loadingText}</span>
                                    </button>
                                </li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </div >
    );
};

export default SingleSelectPlain;
